import { useEffect, useState } from "react"
import { ZIndex } from "constants/zIndex"
import { BootProgress } from "components/new-stuff/ui/Skeleton"

/**
 * How long a route's code may take to arrive before the wait is acknowledged.
 * Most chunks land well inside this, especially once prefetched, so on a
 * normal navigation nothing is drawn here at all.
 */
export const ROUTE_FALLBACK_DELAY_MS = 300

/**
 * A progress bar that appears only once a wait has outlasted
 * ROUTE_FALLBACK_DELAY_MS, so a fast load never flashes it.
 *
 * For any fallback standing in for content whose shape is not known yet - a
 * lazy route, or a lazy tab inside a page. Such content draws its own accurate
 * skeleton the moment it mounts, so the fallback must not promise a layout.
 */
export const DelayedProgress = () => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(
      () => setVisible(true),
      ROUTE_FALLBACK_DELAY_MS
    )
    return () => window.clearTimeout(timer)
  }, [])

  return visible ? <BootProgress /> : null
}

/**
 * What fills the content area while a lazy route's code downloads.
 *
 * It used to draw a generic page skeleton: eight grey lines, the same for
 * every screen. That shape matched no real page, and on table pages it was
 * followed a moment later by the table's own skeleton, so a single navigation
 * showed two different placeholders back to back. Most table pages already
 * draw an accurate skeleton of their own the moment their component exists;
 * this was the only thing standing in front of it.
 *
 * So it no longer claims a shape. The content area is a plain sheet, and the
 * page's own skeleton is the first structure anyone sees. If the code is slow
 * to arrive, a thin progress bar appears after a short delay, which says work
 * is happening without promising a layout. The delay keeps a fast navigation
 * from flashing the bar at all.
 */
export const RouteFallback = () => (
  <div
    className="fixed right-0 top-20 bottom-0 left-0 md:left-[var(--sidenav-w)] transition-[left] duration-300 ease-in-out motion-reduce:transition-none h-[calc(100vh-80px)] overflow-hidden bg-white"
    style={{ zIndex: ZIndex.LoadingOverlay }}
    aria-busy="true"
  >
    <DelayedProgress />
  </div>
)

export default RouteFallback
